import { useEffect, useRef } from "react";

import type { PulseProfileOption } from "@/api/pulse";
import { assignStorySlots } from "@/lib/assignStorySlots";
import type { PulseCard, WeeklyFeature } from "@/types";

import PulseEditorialTagline from "./PulseEditorialTagline";
import PulseProfileSwitch from "./PulseProfileSwitch";
import PulseMoreFeaturedHeading from "./PulseMoreFeaturedHeading";
import PulseStoryCard from "./PulseStoryCard";
import PulseWeeklyHighlights from "./PulseWeeklyHighlights";
import InviteSection from "./InviteSection";

interface Props {
  cards: PulseCard[];
  weeklyFeatures?: WeeklyFeature[];
  selectedDate?: string;
  weeklyLoading?: boolean;
  loading?: boolean;
  availableProfiles?: PulseProfileOption[];
  activeProfileSlug?: string;
  onChangeProfile?: (slug: string) => void;
  onSelectCard: (card: PulseCard, sourceBg?: string) => void;
}

export default function EditorialMain({
  cards,
  weeklyFeatures = [],
  selectedDate,
  weeklyLoading = false,
  loading = false,
  availableProfiles = [],
  activeProfileSlug,
  onChangeProfile,
  onSelectCard,
}: Props) {
  const grainCanvasRef = useRef<HTMLCanvasElement>(null);
  const slots = assignStorySlots(cards);

  useEffect(() => {
    const canvas = grainCanvasRef.current;
    const panel = canvas?.parentElement;
    if (!canvas || !panel) return;

    const density = 0.65;
    const alphaMax = 90;

    const renderGrain = () => {
      if (!String(canvas.getContext).includes("[native code]")) return;

      const width = Math.max(1, Math.ceil(panel.clientWidth));
      const height = Math.max(1, Math.ceil(panel.clientHeight));
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;

      let context: CanvasRenderingContext2D | null = null;
      try {
        context = canvas.getContext("2d");
      } catch {
        return;
      }
      if (!context) return;

      const image = context.createImageData(width, height);
      const { data } = image;
      for (let i = 0; i < data.length; i += 4) {
        if (Math.random() < density) {
          data[i] = 255;
          data[i + 1] = 255;
          data[i + 2] = 255;
          data[i + 3] = (Math.random() * alphaMax) | 0;
        }
      }
      context.putImageData(image, 0, 0);
    };

    renderGrain();
    const resizeObserver = new ResizeObserver(renderGrain);
    resizeObserver.observe(panel);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <main
      id="lectura"
      className={`mx-auto w-full max-w-[1728px] max-md:px-0 md:px-8 lg:px-10 ${loading ? "is-pulse-loading" : ""}`}
    >
      <PulseProfileSwitch
        profiles={availableProfiles}
        activeSlug={activeProfileSlug}
        onChange={onChangeProfile}
      />
      <section className="featured-section">
        <div className="pulse-editorial-grid pulse-editorial-grid--featured">
          {slots.featured.map((card, i) => (
            <PulseStoryCard
              key={`featured-${i}`}
              card={card}
              slotIndex={i}
              layout="featured"
              loading={loading}
              onSelect={onSelectCard}
            />
          ))}
        </div>
      </section>

      <div className="pulse-editorial-panel">
        <div className="pulse-editorial-panel__content" data-node-id="1513:22130">
          <canvas
            ref={grainCanvasRef}
            className="pulse-editorial-panel__grain"
            aria-hidden
          />

          <div className="pulse-featured-tagline-slot">
            <PulseEditorialTagline />
          </div>

          <section className="duo-news">
            <div className="pulse-editorial-inset pulse-editorial-grid pulse-editorial-grid--duo">
              {slots.duo.map((card, i) => (
                <PulseStoryCard
                  key={`duo-${i}`}
                  card={card}
                  slotIndex={4 + i}
                  layout="duo"
                  loading={loading}
                  onSelect={onSelectCard}
                />
              ))}
            </div>
          </section>

          <section className="layered-news mt-[var(--pulse-duo-to-layered-gap)]">
            <div className="layered-news__headline mb-5 flex items-center justify-center">
              <PulseStoryCard
                card={slots.layeredHeadline}
                slotIndex={6}
                layout="headline"
                loading={loading}
                onSelect={onSelectCard}
              />
            </div>
            <div className="pulse-editorial-inset pulse-editorial-grid pulse-editorial-grid--duo">
              {slots.layeredBottom.map((card, i) => (
                <PulseStoryCard
                  key={`layered-${i}`}
                  card={card}
                  slotIndex={7 + i}
                  layout="layered-bottom"
                  loading={loading}
                  onSelect={onSelectCard}
                />
              ))}
            </div>
          </section>
        </div>
      </div>

      <div className="mt-[var(--pulse-layered-to-more-gap)] pulse-more-featured-spacing">
        <PulseMoreFeaturedHeading />
      </div>

      <section className="featured-grid--next pulse-featured-next-spacing mt-[var(--pulse-more-to-featured-gap)]">
        <div className="pulse-editorial-grid pulse-editorial-grid--featured-next">
          {slots.featuredNext.map((card, i) => (
            <PulseStoryCard
              key={`featured-next-${i}`}
              card={card}
              slotIndex={9 + i}
              layout="featured-next"
              loading={loading}
              onSelect={onSelectCard}
            />
          ))}
        </div>
      </section>

      <PulseWeeklyHighlights
        features={weeklyFeatures}
        fallbackDate={selectedDate}
        loading={weeklyLoading}
        onSelectCard={onSelectCard}
      />

      <section id="audio" className="hidden" aria-hidden />

      <InviteSection />
    </main>
  );
}
